import axios from 'axios';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && token !== 'null' && token !== 'undefined') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.url?.includes('/auth/login')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export const auth = {
  login: (email: string, password: string) => api.post('/auth/login', { email, password }),
  register: (data: any) => api.post('/auth/register', data),
};

export const users = {
  list: () => api.get('/users'),
  getHistory: () => api.get('/users/history'),
  create: (data: { email: string; password?: string; fullName: string; role: string }) => api.post('/users', data),
  updateRole: (userId: string, role: string) => api.put('/users/role', { userId, role }),
  toggleActive: (userId: string, isActive: boolean) => api.put('/users/active', { userId, isActive }),
};

export const portfolio = {
  getHoldings: () => api.get('/portfolio/holdings'),
  getTransactions: () => api.get('/portfolio/transactions'),
  buy: (data: { assetId: string; quantity: number; price: number; fee?: number }) => api.post('/portfolio/buy', data),
  sell: (data: { assetId: string; quantity: number; price: number; fee?: number }) => api.post('/portfolio/sell', data),
  bulkImport: (trades: any[]) => api.post('/portfolio/bulk-import', { trades }),
  updateTransaction: (id: string, data: { quantity: number; price: number; fee?: number; type?: string }) => api.put(`/portfolio/transactions/${id}`, data),
  deleteTransaction: (id: string) => api.delete(`/portfolio/transactions/${id}`),
  sendDailyReport: (recipient: string) => api.post('/portfolio/send-daily-report', { recipient }),
};

export const market = {
  getAssets: () => api.get('/market/assets'),
  createAsset: (data: any) => api.post('/market/assets', data),
  updateAsset: (id: string, data: any) => api.put(`/market/assets/${id}`, data),
  deleteAsset: (id: string) => api.delete(`/market/assets/${id}`),
  getAlerts: () => api.get('/market/alerts'),
  createAlert: (data: any) => api.post('/market/alerts', data),
  deleteAlert: (id: string) => api.delete(`/market/alerts/${id}`),
  getOrderBook: (assetId: string) => api.get(`/market/orderbook?assetId=${assetId}`),
  getFxRate: () => api.get('/market/fx-rate'),
  resolveIsin: (isin: string) => api.get(`/market/resolve-isin?isin=${encodeURIComponent(isin)}`),
  getSparklines: () => api.get('/market/sparklines'),
  getHistory: (assetId?: string, symbol?: string, limit: number = 60, timeframe?: string) =>
    api.get(`/market/history?limit=${limit}${assetId ? `&assetId=${assetId}` : ''}${symbol ? `&symbol=${encodeURIComponent(symbol)}` : ''}${timeframe ? `&timeframe=${encodeURIComponent(timeframe)}` : ''}`),
  getLiveNews: () => api.get('/market/live-news'),
};

export const analytics = {
  getPerformance: () => api.get('/analytics/performance'),
  getHistorical: () => api.get('/analytics/historical'),
  getDeals: () => api.get('/analytics/deals'),
};

export const ordersApi = {
  getOrders: () => api.get('/orders'),
  createOrder: (data: {
    assetId: string;
    symbol: string;
    type: 'BUY' | 'SELL';
    quantity: number;
    priceType?: 'MARKET' | 'LIMIT';
    limitPrice?: number;
    currency?: string;
    notes?: string;
  }) => api.post('/orders', data),
  executeOrder: (id: string, data: { executedPrice: number; executedFee?: number; notes?: string }) =>
    api.post(`/orders/${id}/execute`, data),
  cancelOrder: (id: string) => api.post(`/orders/${id}/cancel`),
};

export const adminApi = {
  getTelegramSettings: () => api.get('/admin/telegram'),
  updateTelegramSettings: (data: { botToken: string; chatId: string; isEnabled: boolean }) =>
    api.post('/admin/telegram', data),
  testTelegramAlert: () => api.post('/admin/telegram/test'),

  getCashBalances: () => api.get('/admin/cash'),
  updateCashBalance: (data: { currency: string; amount: number; type: 'SET' | 'DEPOSIT' | 'WITHDRAW'; note?: string }) =>
    api.post('/admin/cash', data),

  getBackups: () => api.get('/admin/backups'),
  createBackup: () => api.post('/admin/backups'),

  getPortfolioGoal: () => api.get('/admin/portfolio-goal'),
  updatePortfolioGoal: (data: { targetType: 'percent' | 'fixed'; targetValue: number; annualHorizon?: number }) =>
    api.post('/admin/portfolio-goal', data),
};



